import React from 'react';
import { Info, Check, Minus } from 'lucide-react';

const PricingGrid = () => {
  const features = [
    {
      category: "InterviewAdvantage.ai",
      items: [
        {
          name: "InterviewAdvantage Reports",
          description: "In-depth reports on your interviewer, company, questions you can expect, how to answer, and questions you must ask. You will know exactly what to expect and how to prepare.",
          student: 1,
          basic: 1,
          plus: 3,
          platinum: 10          
        },
        {
          name: "Advanced AI Interview Prep",
          description: "Cutting edge AI interview prep combined with our 20+ years of experience so you can land your dream job.",
          student: true,
          basic: true,
          plus: true,
          platinum: true          
        },
        {
          name: "Interview Personality Insights",
          description: "Discover on a deeper level how to connect with every interviewer in their unique way so you can set yourself apart from your competition.",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        }
      ]
    },    
    {
      category: "Finding Jobs & Getting Found",
      items: [
        {
          name: "92 Niche Job Boards",
          description: "Unlock access to 92 hidden-gem job boards across every industry imaginable and beat the competition to coveted positions that never hit the mainstream sites.",
          student: false,
          basic: false,
          plus: true,
          platinum: true 
        },
        {
          name: "Applicant Tracking System Hacks",
          description: "Ensure your resume beats the ATS filter and gets seen by real decision-makers. Proven tricks to optimize your resume.",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        },
        {
          name: "LinkedIn Profile Hacks",
          description: "87% of companies and 97% of recruiters use LinkedIn to find candidates. These hacks optimize your profile for job searching.",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        },
        {
          name: "LinkedIn Optimization Checklist",
          description: "Get found on LinkedIn by recruiters with a profile that shines. Maximize your visibility and connections.",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        }
    
      ]
    },
    {
      category: "Interview Preparedness",
      items: [
        {
          name: "Throwing the First Punch®",
          description: "Flip the script in your interview by steering the conversation. Learn how to set yourself apart immediately. Nobody else will show you this!",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        },
        {
          name: "Ultimate Answer Formula®: Turn Interviews Into Offers",
          description: "Normally reserved for 1:1 coaching - Learn the proven formula for delivering concise, impactful answers that captivate interviewers and turn opportunities into offers.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        },
        {
          name: "Trick and Trap Questions",
          description: "Questions designed to trick you and how you must answer to avoid getting disqualified.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        },
        {
          name: "Interview Manager Style Guide",
          description: "Discover how to find the interviewer's style and tone. You will know exactly how to respond to any interviewer's style.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        },
        {
          name: "Closing the Interview Guide",
          description: "Seal the deal with confidence. Discover exactly how to leave a lasting impression at the end of your interview so you advance to the next step.",
          student: false,
          basic: false,
          plus: true,
          platinum: true 
        },
      ]
    },
    {
      category: "Resume Advantage",
      items: [
        {
          name: "2025 Resume Advantage Checklist",
          description: "Your roadmap to crafting a resume that opens doors. Ensure every element is optimized to beat the competition.",
          student: true,
          basic: true,
          plus: true,
          platinum: true          
        },
        {
          name: "Top Ten Resume Mistakes Checklist",
          description: "Avoid costly errors that keep your resume at the bottom of the pile. Fix the most common mistakes now!",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        },
        {
          name: "Resume Rule of Thirds",
          description: "Unlock the secret of the top third: make your first impression count with this simple yet powerful technique.",
          student: true,
          basic: true,
          plus: true,
          platinum: true 
        }
      ]
    },
    {
      category: "Data-Driven Resumes",
      items: [
        {
          name: "Quantifiable Results Worksheet",
          description: "Turn your achievements into numbers that matter. Impress with data-driven bullet points.",
          student: false,
          basic: false,
          plus: true,
          platinum: true 
        },
        {
          name: "Nailing the Curiosity Driven Resume",
          description: "Spark interest and secure interviews by crafting a resume that leaves them wanting more.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        }
      ]
    },
    {
      category: "Job Offer Negotiation",
      items: [
        {
          name: "Negotiation Do's and Don'ts",
          description: "Master the art of negotiating job offers. Avoid pitfalls and get the compensation you deserve.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        },          
        {
          name: "How to Answer Salary Questions",
          description: "Get a great offer by knowing how to answer salary questions. Don't lose thousands of dollars by not knowing how to answer.",
          student: false,
          basic: false,
          plus: false,
          platinum: true 
        }
      ]
    }
  ];

  const PlanIcon = ({ available }) => {
    if (typeof available === 'number') {
      return <span className="text-gray-900 font-medium mx-auto text-center block">{available}</span>;
    }
    
    if (available) {
      return <Check className="w-6 h-6 text-green-600 mx-auto" />;
    }
    return <Minus className="w-6 h-6 text-gray-300 mx-auto" />;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <a name="compare-all-benefits"></a>
      <h2 className="text-4xl font-bold text-center mb-4">Compare all benefits</h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="w-1/3 py-4 px-6 text-left">Benefit</th>
              <th className="w-1/6 py-4 px-6 text-center">Student</th>
              <th className="w-1/6 py-4 px-6 text-center">Basic</th>
              <th className="w-1/6 py-4 px-6 text-center">Plus</th>
              <th className="w-1/6 py-4 px-6 text-center">Platinum</th>
            </tr>
          </thead>
          <tbody>
            {features.map((category, categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                <tr className="bg-gray-50">
                  <td colSpan={5} className="py-3 px-6 font-medium">
                    {category.category}
                  </td>
                </tr>
                {category.items.map((feature, featureIndex) => (
                  <tr key={`${categoryIndex}-${featureIndex}`} className="border-b hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{feature.name}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <PlanIcon available={feature.student} />
                    </td>
                    <td className="py-4 px-6">
                      <PlanIcon available={feature.basic} />
                    </td>
                    <td className="py-4 px-6">
                      <PlanIcon available={feature.plus} />
                    </td>
                    <td className="py-4 px-6">
                      <PlanIcon available={feature.platinum} />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PricingGrid;